import React, { useEffect, useMemo, useRef, useState } from "react";
import { MS_TO_KNOTS, PullTestResult } from "@/services/PushStationService";
import ActionsMenu from "@/components/common/ActionsMenu";
import {
  DIR_FORMATS,
  FlatField,
  JsonDirFormat,
  JsonPick,
  JsonPullMapping,
  JsonPullPreview,
  JsonTimeMode,
  JsonWindUnit,
  MAC_PRESET,
  PICK_RULES,
  STALE_MS,
  SURFR_STANDARD_PRESET,
  TIME_MODES,
  TIME_MODE_LABELS,
  WIND_UNITS,
  WIND_UNIT_LABELS,
  applyToRecord,
  browserTimeZone,
  cloneMapping,
  degToCompass,
  describeErrorCode,
  flattenRecord,
  formatInZone,
  guessFieldsFromRecord,
  listTimeZones,
  normalizeMapping,
  toEpochMs,
  validateMapping,
} from "@/utils/jsonMapping";

/**
 * Visual configurator for the `json` pull provider's field mapping (contract:
 * docs/plan/live-wind-pull-json/01-json-mapping-contract.md, "Configurator UX").
 *
 * Controlled: the parent owns the mapping and the server test (it knows the URL, the
 * station ref and the stale-response guards). This component owns only what the user sees
 * while building the mapping — the last fetched record, the flattened field list, the raw
 * JSON editor draft — and asks the parent to run tests through `onTest`.
 *
 * Save gating lives with the parent (`verified`): the server must have returned ok for the
 * CURRENT url + mapping. The local preview here is instant feedback, never a substitute.
 *
 * Testing is AUTOMATIC (Ali, 2026-10-29): the parent re-tests, debounced, whenever the url
 * or a complete mapping changes, and shows one status line next to its footer. So this
 * component has no "Re-test" button and no verdict box of its own — only "Fetch &
 * auto-detect" (the initial fetch, later "Re-fetch fields") and a compact Presets menu.
 */

const inputCls =
  "w-full rounded bg-gray-700 border border-gray-600 px-3 py-2 text-white focus:outline-none focus:border-accent";
const smallInputCls =
  "w-full rounded bg-gray-700 border border-gray-600 px-2 py-1.5 text-sm text-white focus:outline-none focus:border-accent";
const labelCls = "mb-1 block text-xs text-white75";
const btnCls =
  "rounded bg-gray-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-gray-600 disabled:opacity-50";

export interface JsonMappingConfiguratorProps {
  url: string;
  mapping: JsonPullMapping | null;
  onMappingChange: (m: JsonPullMapping | null) => void;
  /**
   * Run a server pull-test. `undefined` options = "Fetch" (the server returns a suggested
   * mapping). Resolves to null when the response was superseded / the dialog closed.
   */
  onTest: (options: JsonPullMapping | undefined) => Promise<PullTestResult | null>;
  testing: boolean;
  /** Latest server result (may belong to an older mapping — see `verified`). */
  result: PullTestResult | null;
  /** True iff `result` is ok AND was produced for the current url + mapping. */
  verified: boolean;
  /** Catalogue `defaultOptions` — the "Surfr standard" preset as the backend defines it. */
  defaultOptions?: JsonPullMapping | null;
  /** Default IANA zone for local time (station location if known, else the browser's). */
  defaultTz?: string | null;
  /**
   * Run one server test as soon as the configurator mounts (edit mode), so the field list
   * is populated without a click: a valid stored mapping is re-tested with its options;
   * otherwise the URL is fetched for fields only (the stored mapping is never replaced).
   */
  autoRunOnMount?: boolean;
}

const fmtValue = (v: unknown): string => {
  if (v === null) return "null";
  if (typeof v === "string") return JSON.stringify(v);
  return String(v);
};

const kn = (ms: number | null | undefined) =>
  ms == null || !Number.isFinite(ms) ? "—" : `${(ms * MS_TO_KNOTS).toFixed(1)} kn`;
const msText = (ms: number | null | undefined) =>
  ms == null || !Number.isFinite(ms) ? "—" : `${ms.toFixed(2)} m/s`;

const ageText = (ageMs: number): string => {
  const neg = ageMs < 0;
  const a = Math.abs(ageMs);
  const mins = Math.round(a / 60000);
  const txt = mins < 90 ? `${mins} min` : `${(a / 3600000).toFixed(1)} h`;
  return neg ? `${txt} in the FUTURE` : `${txt} ago`;
};

/** Field dropdown fed by the flattened record; keeps an unknown current value selectable. */
const PathSelect: React.FC<{
  id: string;
  value: string;
  fields: FlatField[];
  onChange: (p: string) => void;
  placeholder: string;
  allowNone?: boolean;
}> = ({ id, value, fields, onChange, placeholder, allowNone }) => {
  const known = fields.some((f) => f.path === value);
  return (
    <select id={id} className={smallInputCls} value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">{allowNone ? "— none —" : placeholder}</option>
      {value && !known && <option value={value}>{value} (not in fetched record)</option>}
      {fields.map((f) => (
        <option key={f.path} value={f.path}>
          {f.path} = {fmtValue(f.value).slice(0, 40)}
        </option>
      ))}
    </select>
  );
};

const UnitSelect: React.FC<{
  id: string;
  value: JsonWindUnit | null;
  onChange: (u: JsonWindUnit | null) => void;
}> = ({ id, value, onChange }) => (
  <select
    id={id}
    className={`${smallInputCls} ${value ? "" : "border-amber-500"}`}
    value={value ?? ""}
    onChange={(e) => onChange((e.target.value || null) as JsonWindUnit | null)}
  >
    {/* No silent default unit: a wrong unit is the commonest silent data bug. */}
    <option value="">— pick unit —</option>
    {WIND_UNITS.map((u) => (
      <option key={u} value={u}>
        {WIND_UNIT_LABELS[u]}
      </option>
    ))}
  </select>
);

const JsonMappingConfigurator: React.FC<JsonMappingConfiguratorProps> = ({
  url,
  mapping,
  onMappingChange,
  onTest,
  testing,
  result,
  verified,
  defaultOptions,
  defaultTz,
  autoRunOnMount,
}) => {
  const tzDefault = defaultTz || browserTimeZone() || "UTC";
  const [preview, setPreview] = useState<JsonPullPreview | null>(null);
  const [rawOpen, setRawOpen] = useState(false);
  const [rawText, setRawText] = useState("");
  const [rawErrors, setRawErrors] = useState<string[]>([]);
  const [now, setNow] = useState(() => Date.now());

  // Keep the record (and its field list) from the newest result that carried one, even
  // once the mapping changes — the user is editing against it.
  useEffect(() => {
    if (result?.preview) setPreview(result.preview);
  }, [result]);

  // Age ticks so a long editing session does not show a frozen "2 min ago".
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 30000);
    return () => window.clearInterval(id);
  }, []);

  // Keep the raw editor in sync with the visual editor while it is not being edited.
  useEffect(() => {
    if (!rawOpen) setRawText(mapping ? JSON.stringify(normalizeMapping(mapping), null, 2) : "");
  }, [mapping, rawOpen]);

  const fields = useMemo(() => (preview ? flattenRecord(preview.record) : []), [preview]);
  const timeZones = useMemo(() => listTimeZones(), []);

  const update = (fn: (m: JsonPullMapping) => void) => {
    if (!mapping) return;
    const next = cloneMapping(mapping);
    fn(next);
    onMappingChange(next);
  };

  const urlOk = url.trim().startsWith("https://");

  const runFetch = async (opts: { keepMapping?: boolean } = {}) => {
    const res = await onTest(undefined);
    if (!res) return;
    if (!res.preview) {
      // The body did not parse as JSON (or an older backend) — nothing to map against.
      return;
    }
    setPreview(res.preview);
    if (opts.keepMapping && mapping) return;
    const guess = res.preview.suggested
      ? cloneMapping(res.preview.suggested)
      : guessFieldsFromRecord(res.preview.record, tzDefault);
    if (guess.time?.mode === "local" && !guess.time.tz) guess.time.tz = tzDefault;
    if (
      mapping &&
      !window.confirm("Replace the current mapping with the auto-detected one?\n\nCancel keeps your mapping and only refreshes the field list.")
    ) {
      return;
    }
    // A complete guess is tested by the parent's auto-test; nothing to trigger here.
    onMappingChange(guess);
  };

  // Edit mode (see autoRunOnMount). A COMPLETE stored mapping needs nothing here — the
  // parent's auto-test runs it and its result carries the record for the field list. An
  // incomplete one cannot be tested, so fetch the fields only. Mount-only by design.
  const autoRan = useRef(false);
  useEffect(() => {
    if (!autoRunOnMount || autoRan.current || !urlOk) return;
    autoRan.current = true;
    if (!mapping || validateMapping(normalizeMapping(mapping)).length > 0) {
      void runFetch({ keepMapping: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRunOnMount]);

  const applyPreset = (m: JsonPullMapping) => {
    if (mapping && !window.confirm("Replace the current mapping with this preset?")) return;
    onMappingChange(cloneMapping(m));
  };

  const reguessLocally = () => {
    if (!preview) return;
    const g = guessFieldsFromRecord(preview.record, tzDefault, {
      records: mapping?.records ?? "",
      pick: mapping?.pick ?? "newest",
    });
    onMappingChange(g);
  };

  const applyRaw = () => {
    let parsed: unknown;
    try {
      parsed = JSON.parse(rawText);
    } catch (e) {
      setRawErrors([`Not valid JSON: ${(e as Error).message}`]);
      return;
    }
    const errs = validateMapping(parsed);
    if (errs.length) {
      setRawErrors(errs);
      return;
    }
    setRawErrors([]);
    onMappingChange(normalizeMapping(parsed as JsonPullMapping));
  };

  // ---- Live preview -------------------------------------------------------------------
  const localReading = useMemo(() => {
    if (!mapping || !preview) return null;
    return applyToRecord(preview.record, mapping, now, { checkFreshness: false });
  }, [mapping, preview, now]);

  const serverSample = verified && result?.ok ? result.sample ?? null : null;
  const shown = serverSample
    ? {
        windMs: serverSample.windMs,
        gustMs: serverSample.gustMs,
        dirDeg: serverSample.dirDeg,
        observedAt: toEpochMs(serverSample.observedAt),
        error: null as string | null,
      }
    : localReading;
  const displayTz = mapping?.time.mode === "local" && mapping.time.tz ? mapping.time.tz : tzDefault;
  const age = shown?.observedAt != null ? now - shown.observedAt : null;
  // Negative age beyond a minute of clock skew is as wrong as a stale one (wrong zone).
  const ageBad = age != null && (age > STALE_MS || age < -60000);

  const missingUnit = mapping != null && (!mapping.wind.unit || (mapping.gust?.path && !mapping.gust.unit));

  return (
    <div data-testid="json-mapping-configurator" className="space-y-3 rounded border border-gray-700 bg-gray-900/40 p-3">
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" className={btnCls} onClick={() => void runFetch()} disabled={testing || !urlOk}>
          {preview ? "Re-fetch fields" : "Fetch & auto-detect"}
        </button>
        <ActionsMenu
          label="Presets"
          buttonText="Presets"
          variant="dropdown"
          items={[
            { label: "Surfr standard", onSelect: () => applyPreset(defaultOptions ?? SURFR_STANDARD_PRESET) },
            { label: "MAC-style (date+time local)", onSelect: () => applyPreset(MAC_PRESET) },
            ...(preview
              ? [{ label: "Re-guess from fetched record", onSelect: reguessLocally, dividerBefore: true }]
              : []),
          ]}
        />
        <span className="text-xs text-gray-300">
          Changes are tested automatically — see the status line at the bottom.
        </span>
      </div>
      {!urlOk && <p className="text-xs text-amber-300">Enter an https URL above, then Fetch.</p>}

      {preview && (
        <details className="rounded border border-gray-700 p-2" open={!mapping}>
          <summary className="cursor-pointer text-xs text-white75">
            Fetched record — {fields.length} field{fields.length === 1 ? "" : "s"}
            {preview.recordCount != null ? ` · ${preview.recordCount} records in the array` : " · single object"}
          </summary>
          <table data-testid="json-fields" className="mt-2 w-full text-xs">
            <tbody>
              {fields.map((f) => (
                <tr key={f.path} className="border-t border-gray-800">
                  <td className="py-0.5 pr-2 font-mono text-white">{f.path}</td>
                  <td className="py-0.5 font-mono text-white75 break-all">{fmtValue(f.value)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      )}

      {!mapping ? (
        <p className="text-sm text-white75">Fetch the URL to auto-detect the fields, or start from a preset.</p>
      ) : (
        <div className="space-y-3">
          {/* Records */}
          <div className="grid grid-cols-3 gap-2">
            <div className="col-span-2">
              <label className={labelCls} htmlFor="jm-records">
                Records path ("" = document root)
              </label>
              <input
                id="jm-records"
                className={smallInputCls}
                value={mapping.records}
                onChange={(e) => update((m) => (m.records = e.target.value.trim()))}
                placeholder="(root)"
              />
            </div>
            <div>
              <label className={labelCls} htmlFor="jm-pick">
                Pick (when an array)
              </label>
              <select
                id="jm-pick"
                className={smallInputCls}
                value={mapping.pick}
                onChange={(e) => update((m) => (m.pick = e.target.value as JsonPick))}
              >
                {PICK_RULES.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            </div>
            <p className="col-span-3 text-xs text-white50">
              {preview
                ? preview.recordCount != null
                  ? `${preview.recordCount} records (root is an ${preview.rootKind}); the "${mapping.pick}" one is shown below.`
                  : `Records resolve to a single object (root is an ${preview.rootKind}).`
                : "Fetch to see the record count."}{" "}
              Changing the records path refreshes the field list after the automatic re-test.
            </p>
          </div>

          {/* Wind / gust */}
          <div className="grid grid-cols-3 gap-2">
            <div className="col-span-2">
              <label className={labelCls} htmlFor="jm-wind">
                Wind (required)
              </label>
              <PathSelect
                id="jm-wind"
                value={mapping.wind.path}
                fields={fields}
                placeholder="— choose the wind field —"
                onChange={(p) => update((m) => (m.wind.path = p))}
              />
            </div>
            <div>
              <label className={labelCls} htmlFor="jm-wind-unit">
                Wind unit
              </label>
              <UnitSelect id="jm-wind-unit" value={mapping.wind.unit} onChange={(u) => update((m) => (m.wind.unit = u))} />
            </div>
            <div className="col-span-2">
              <label className={labelCls} htmlFor="jm-gust">
                Gust (optional)
              </label>
              <PathSelect
                id="jm-gust"
                value={mapping.gust?.path ?? ""}
                fields={fields}
                allowNone
                placeholder=""
                onChange={(p) =>
                  update((m) => {
                    m.gust = p ? { path: p, unit: m.gust?.unit ?? null } : null;
                  })
                }
              />
            </div>
            <div>
              <label className={labelCls} htmlFor="jm-gust-unit">
                Gust unit
              </label>
              {mapping.gust?.path ? (
                <UnitSelect
                  id="jm-gust-unit"
                  value={mapping.gust.unit}
                  onChange={(u) => update((m) => (m.gust = { path: m.gust!.path, unit: u }))}
                />
              ) : (
                <p className="py-1.5 text-xs text-white50">no gust</p>
              )}
            </div>
          </div>

          {/* Direction */}
          <div className="grid grid-cols-3 gap-2">
            <div className="col-span-2">
              <label className={labelCls} htmlFor="jm-dir">
                Direction (optional)
              </label>
              <PathSelect
                id="jm-dir"
                value={mapping.dir?.path ?? ""}
                fields={fields}
                allowNone
                placeholder=""
                onChange={(p) =>
                  update((m) => {
                    m.dir = p ? { path: p, format: m.dir?.format ?? "deg" } : null;
                  })
                }
              />
            </div>
            <div>
              <label className={labelCls} htmlFor="jm-dir-format">
                Direction format
              </label>
              <select
                id="jm-dir-format"
                className={smallInputCls}
                value={mapping.dir?.format ?? "deg"}
                disabled={!mapping.dir?.path}
                onChange={(e) => update((m) => m.dir && (m.dir.format = e.target.value as JsonDirFormat))}
              >
                {DIR_FORMATS.map((f) => (
                  <option key={f} value={f}>
                    {f === "deg" ? "degrees" : "compass (N, NNE…)"}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Time */}
          <div className="grid grid-cols-3 gap-2">
            <div className="col-span-3">
              <label className={labelCls} htmlFor="jm-time-mode">
                Time
              </label>
              <select
                id="jm-time-mode"
                className={smallInputCls}
                value={mapping.time.mode}
                onChange={(e) =>
                  update((m) => {
                    const mode = e.target.value as JsonTimeMode;
                    m.time = { ...m.time, mode };
                    if (mode === "local" && !m.time.tz) m.time.tz = tzDefault;
                  })
                }
              >
                {TIME_MODES.map((t) => (
                  <option key={t} value={t}>
                    {TIME_MODE_LABELS[t]}
                  </option>
                ))}
              </select>
            </div>
            {mapping.time.mode !== "fetch" && mapping.time.mode !== "local" && (
              <div className="col-span-3">
                <label className={labelCls} htmlFor="jm-time-path">
                  Time field
                </label>
                <PathSelect
                  id="jm-time-path"
                  value={mapping.time.path ?? ""}
                  fields={fields}
                  placeholder="— choose the time field —"
                  onChange={(p) => update((m) => (m.time.path = p || null))}
                />
              </div>
            )}
            {mapping.time.mode === "local" && (
              <>
                <div className="col-span-3">
                  <label className={labelCls} htmlFor="jm-time-local-src">
                    Local time source
                  </label>
                  <select
                    id="jm-time-local-src"
                    className={smallInputCls}
                    value={mapping.time.path ? "single" : "pair"}
                    onChange={(e) =>
                      update((m) => {
                        if (e.target.value === "single") {
                          m.time = { mode: "local", path: m.time.path || fields[0]?.path || "", tz: m.time.tz };
                        } else {
                          m.time = { mode: "local", datePath: m.time.datePath ?? "", timePath: m.time.timePath ?? "", tz: m.time.tz };
                        }
                      })
                    }
                  >
                    <option value="single">One field (YYYY-MM-DD HH:MM[:SS])</option>
                    <option value="pair">Separate date + time fields</option>
                  </select>
                </div>
                {mapping.time.path ? (
                  <div className="col-span-2">
                    <label className={labelCls} htmlFor="jm-time-path">
                      Date-time field
                    </label>
                    <PathSelect
                      id="jm-time-path"
                      value={mapping.time.path ?? ""}
                      fields={fields}
                      placeholder="— choose —"
                      onChange={(p) => update((m) => (m.time.path = p || null))}
                    />
                  </div>
                ) : (
                  <>
                    <div>
                      <label className={labelCls} htmlFor="jm-time-date">
                        Date field
                      </label>
                      <PathSelect
                        id="jm-time-date"
                        value={mapping.time.datePath ?? ""}
                        fields={fields}
                        placeholder="— date —"
                        onChange={(p) => update((m) => (m.time.datePath = p || null))}
                      />
                    </div>
                    <div>
                      <label className={labelCls} htmlFor="jm-time-time">
                        Time-of-day field
                      </label>
                      <PathSelect
                        id="jm-time-time"
                        value={mapping.time.timePath ?? ""}
                        fields={fields}
                        placeholder="— time —"
                        onChange={(p) => update((m) => (m.time.timePath = p || null))}
                      />
                    </div>
                  </>
                )}
                <div>
                  <label className={labelCls} htmlFor="jm-time-tz">
                    Time zone
                  </label>
                  <select
                    id="jm-time-tz"
                    className={smallInputCls}
                    value={mapping.time.tz ?? ""}
                    onChange={(e) => update((m) => (m.time.tz = e.target.value || null))}
                  >
                    <option value="">— pick —</option>
                    {mapping.time.tz && timeZones.indexOf(mapping.time.tz) < 0 && (
                      <option value={mapping.time.tz}>{mapping.time.tz}</option>
                    )}
                    {timeZones.map((z) => (
                      <option key={z} value={z}>
                        {z}
                      </option>
                    ))}
                  </select>
                </div>
              </>
            )}
            {mapping.time.mode === "fetch" && (
              <p className="col-span-3 text-xs text-amber-300">
                No timestamp: each fetch is stamped with the fetch time, and an unchanged file
                produces no new reading. Prefer a real time field when the source has one.
              </p>
            )}
          </div>

          {/* Live preview */}
          <div
            data-testid="json-live-preview"
            className={`rounded border p-3 text-sm ${ageBad ? "border-red-600 bg-red-900/30" : "border-gray-700 bg-gray-800"}`}
          >
            <p className="mb-1 text-xs font-medium uppercase tracking-wide text-white75">
              {serverSample ? "Preview — confirmed by the server" : "Preview — local, not yet confirmed by the server"}
            </p>
            {missingUnit ? (
              <p className="text-amber-300">Pick the unit the source uses to see the reading.</p>
            ) : !preview && !serverSample ? (
              <p className="text-white75">Fetch to preview the mapped reading.</p>
            ) : shown?.error ? (
              <p className="text-red-300">{describeErrorCode(shown.error)}</p>
            ) : shown ? (
              <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                <div>
                  <dt className="inline text-white50">Wind: </dt>
                  <dd className="inline text-white">
                    {kn(shown.windMs)} · {msText(shown.windMs)}
                  </dd>
                </div>
                <div>
                  <dt className="inline text-white50">Gust: </dt>
                  <dd className="inline text-white">
                    {kn(shown.gustMs)} · {msText(shown.gustMs)}
                  </dd>
                </div>
                <div>
                  <dt className="inline text-white50">Direction: </dt>
                  <dd className="inline text-white">
                    {shown.dirDeg == null ? "—" : `${Math.round(shown.dirDeg)}° ${degToCompass(shown.dirDeg)}`}
                  </dd>
                </div>
                <div>
                  <dt className="inline text-white50">Age: </dt>
                  <dd className={`inline ${ageBad ? "font-bold text-red-300" : "text-white"}`}>
                    {age == null ? "—" : ageText(age)}
                  </dd>
                </div>
                <div className="col-span-2">
                  <dt className="inline text-white50">Observed: </dt>
                  <dd className="inline text-white">
                    {shown.observedAt == null
                      ? "—"
                      : `${formatInZone(shown.observedAt, "UTC")} UTC · ${formatInZone(shown.observedAt, displayTz)} ${displayTz}`}
                  </dd>
                </div>
              </dl>
            ) : null}
            {ageBad && (
              <p data-testid="json-age-warning" className="mt-2 font-semibold text-red-300">
                {age! < 0
                  ? "⚠ The reading is in the FUTURE — the time zone is almost certainly wrong."
                  : "⚠ The reading is more than 2 h old — wrong time zone, wrong unit, or a dead feed. The server will reject it as stale."}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Raw JSON editor for power users */}
      <details
        className="rounded border border-gray-700 p-2"
        open={rawOpen}
        onToggle={(e) => setRawOpen((e.target as HTMLDetailsElement).open)}
      >
        <summary className="cursor-pointer text-xs text-white75">Raw mapping JSON (advanced)</summary>
        <textarea
          aria-label="Raw mapping JSON"
          className={`${inputCls} mt-2 font-mono text-xs`}
          rows={10}
          value={rawText}
          onChange={(e) => setRawText(e.target.value)}
          spellCheck={false}
        />
        {rawErrors.length > 0 && (
          <ul data-testid="json-raw-errors" className="mt-1 list-disc pl-5 text-xs text-red-300">
            {rawErrors.map((e) => (
              <li key={e}>{e}</li>
            ))}
          </ul>
        )}
        <div className="mt-2 flex gap-2">
          <button type="button" className={btnCls} onClick={applyRaw}>
            Apply JSON
          </button>
          <button
            type="button"
            className={btnCls}
            onClick={() => {
              setRawErrors([]);
              setRawText(mapping ? JSON.stringify(normalizeMapping(mapping), null, 2) : "");
            }}
          >
            Reset to current
          </button>
        </div>
      </details>
    </div>
  );
};

export default JsonMappingConfigurator;
